/**
 * Pure aggregation helpers for the Kronos Dashboard.
 *
 * Everything in this module is a plain function over `TimeEntry[]`
 * (and `Designation[]` for the lagging-projects computation). No
 * Firestore SDK, no React, no async — which makes it trivially
 * unit-testable and reusable by Cloud Functions should the same
 * maths land server-side in Phase 9.
 *
 * Related KB:
 *   - `docs/kb/firebase/timeentry-uid-scoped-reads.md`
 *   - `docs/kb/firebase/composite-indexes.md`
 */

import type { Designation, TimeEntry } from '@altyor-kronos/shared-types';

// ─── Date boundary helpers ─────────────────────────────────────────

/**
 * First of the given date's month, at 00:00 local time.
 *
 * The dashboard runs against the user's local calendar — an employee
 * in Paris and one in Toulouse both see the same month boundary, so
 * we never need UTC shifting.
 */
export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

/**
 * Monday 00:00 local of the ISO week containing `d`. Altyor is a
 * French company: the workweek starts on Monday, not Sunday. Always
 * resolves *backwards* — a Sunday returns the previous Monday.
 *
 *   Mon → Mon (unchanged)
 *   Sun → previous Mon (−6 days)
 *   Wed → Mon (−2 days)
 */
export function startOfWeekMonday(d: Date): Date {
  const day = d.getDay(); // 0=Sun, 1=Mon, …, 6=Sat
  // Number of days to subtract to reach the Monday of the current ISO week.
  // For Sunday (0) we subtract 6, otherwise subtract (day - 1).
  const delta = day === 0 ? 6 : day - 1;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - delta, 0, 0, 0, 0);
}

// ─── Small map builder ─────────────────────────────────────────────

/**
 * Build a `Map<K, number>` by summing `value(item)` for each item
 * under its `key(item)` bucket. Generic so the three by-field
 * aggregators below stay one-liners.
 */
export function sumByKey<T, K>(
  items: readonly T[],
  key: (item: T) => K,
  value: (item: T) => number,
): Map<K, number> {
  const out = new Map<K, number>();
  for (const item of items) {
    const k = key(item);
    out.set(k, (out.get(k) ?? 0) + value(item));
  }
  return out;
}

// ─── Entry aggregators ─────────────────────────────────────────────

export function entriesByProject(entries: readonly TimeEntry[]): Map<string, number> {
  return sumByKey(entries, (e) => e.projectId, (e) => e.durationSeconds ?? 0);
}

export function entriesByDesignation(entries: readonly TimeEntry[]): Map<string, number> {
  return sumByKey(entries, (e) => e.designationId, (e) => e.durationSeconds ?? 0);
}

export function entriesByUser(entries: readonly TimeEntry[]): Map<string, number> {
  return sumByKey(entries, (e) => e.userId, (e) => e.durationSeconds ?? 0);
}

// ─── Weekly trend ─────────────────────────────────────────────────

export interface WeekBucket {
  /** Monday 00:00 local of the bucket. */
  weekStart: Date;
  totalSeconds: number;
}

/**
 * Bucket `entries` into `weeksBack` trailing weeks, ending at the
 * week containing `anchor`. Returns buckets in chronological order
 * (oldest first) with zero-filled gaps — the chart consumer never
 * has to reason about missing keys.
 *
 * Entries whose Monday falls outside the window are silently
 * dropped; callers should already have time-filtered with Firestore
 * but we defensively re-filter here.
 */
export function entriesByWeek(
  entries: readonly TimeEntry[],
  anchor: Date,
  weeksBack: number,
): WeekBucket[] {
  if (weeksBack <= 0) return [];

  const currentMonday = startOfWeekMonday(anchor);
  const buckets: WeekBucket[] = [];

  // Walk the calendar in whole days rather than milliseconds so we
  // don't drift across DST transitions (e.g. the European spring-
  // forward shortens a week to 167 hours — subtracting `7*24*3600e3`
  // would land the "oldest" bucket at 23:00 of the previous Sunday).
  for (let i = weeksBack - 1; i >= 0; i--) {
    const weekStart = new Date(
      currentMonday.getFullYear(),
      currentMonday.getMonth(),
      currentMonday.getDate() - i * 7,
      0,
      0,
      0,
      0,
    );
    buckets.push({ weekStart, totalSeconds: 0 });
  }

  // Index buckets by their start millis for O(1) lookup.
  const byStart = new Map<number, WeekBucket>();
  for (const b of buckets) byStart.set(b.weekStart.getTime(), b);

  for (const e of entries) {
    const mon = startOfWeekMonday(e.startTime.toDate()).getTime();
    const bucket = byStart.get(mon);
    if (bucket) {
      bucket.totalSeconds += e.durationSeconds ?? 0;
    }
  }

  return buckets;
}

// ─── Lagging projects (team overview) ──────────────────────────────

export interface LaggingProject {
  projectId: string;
  /** Total hoursSold summed over all designations of this project. */
  hoursSold: number;
  /** Total hoursSpent derived from the supplied `entries`. */
  hoursSpent: number;
  /** hoursSpent / hoursSold. */
  ratio: number;
}

/**
 * Cross-project "am I over budget?" summary for the team overview.
 *
 * Until Phase 9 wires the `hoursSpent` / `percentCompletion` computed
 * fields on each designation, this is computed client-side:
 *   hoursSpent(project) = Σ entry.durationSeconds / 3600
 *                          over entries of that project
 *   hoursSold(project)  = Σ designation.hoursSold
 *                          over designations of that project
 *
 * Projects with `hoursSold == 0` are excluded — we cannot compute a
 * meaningful ratio and surfacing them would just add noise. The
 * result is sorted most-lagging first so the dashboard can slice the
 * top N for display.
 *
 * @param threshold ratio at or above which a project is flagged
 *                  (e.g. 0.7 = show projects at 70% or more of their
 *                  sold hours).
 */
export function laggingProjects(
  designations: ReadonlyArray<{ projectId: string; data: Designation }>,
  entries: readonly TimeEntry[],
  threshold: number,
): LaggingProject[] {
  const soldByProject = sumByKey(
    designations,
    (d) => d.projectId,
    (d) => d.data.hoursSold ?? 0,
  );
  const spentSecondsByProject = entriesByProject(entries);

  const out: LaggingProject[] = [];
  for (const [projectId, hoursSold] of soldByProject.entries()) {
    if (hoursSold <= 0) continue;
    const hoursSpent = hoursFromSeconds(spentSecondsByProject.get(projectId) ?? 0);
    const ratio = hoursSpent / hoursSold;
    if (ratio >= threshold) {
      out.push({ projectId, hoursSold, hoursSpent, ratio });
    }
  }
  out.sort((a, b) => b.ratio - a.ratio);
  return out;
}

// ─── Unit conversion ──────────────────────────────────────────────

export function hoursFromSeconds(seconds: number): number {
  return seconds / 3600;
}

// ─── Daily-stacked bucketing (Phase 7.x) ──────────────────────────

export interface DayBucket {
  /** Local 00:00 of the day. */
  dayStart: Date;
  /** Hours-per-project map for the stacked segments. */
  byProject: Map<string, number>;
  /** Sum across all projects — convenience for the tooltip header. */
  totalSeconds: number;
}

/**
 * Bucket entries into 7 consecutive local days starting at
 * `weekStart` (Mon 00:00). Each bucket carries a per-project
 * breakdown so the dashboard can render a stacked-by-project bar
 * per day — matching the previous iteration's "Mon→Sun stack"
 * visualisation.
 *
 * Entries outside the [weekStart, weekStart+7d) window are silently
 * dropped; callers should already time-filter at the Firestore
 * query level but we defensively re-filter here.
 */
export function entriesByDayOfWeek(
  entries: readonly TimeEntry[],
  weekStart: Date,
): DayBucket[] {
  const buckets: DayBucket[] = [];
  const byStart = new Map<number, DayBucket>();

  // Build 7 buckets Mon→Sun using calendar-component arithmetic so
  // a DST transition inside the week can't shift any day by an hour.
  for (let i = 0; i < 7; i++) {
    const dayStart = new Date(
      weekStart.getFullYear(),
      weekStart.getMonth(),
      weekStart.getDate() + i,
      0,
      0,
      0,
      0,
    );
    const b: DayBucket = { dayStart, byProject: new Map(), totalSeconds: 0 };
    buckets.push(b);
    byStart.set(dayStart.getTime(), b);
  }

  // The "day" an entry belongs to is the local midnight immediately
  // before its startTime — we reconstruct it by zeroing the time
  // components rather than relying on ms maths.
  for (const e of entries) {
    const t = e.startTime.toDate();
    const dayKey = new Date(t.getFullYear(), t.getMonth(), t.getDate(), 0, 0, 0, 0).getTime();
    const bucket = byStart.get(dayKey);
    if (!bucket) continue;
    const dur = e.durationSeconds ?? 0;
    bucket.totalSeconds += dur;
    bucket.byProject.set(e.projectId, (bucket.byProject.get(e.projectId) ?? 0) + dur);
  }

  return buckets;
}

// ─── Nested by-user-by-project map ────────────────────────────────

/**
 * Nested aggregation `uid → projectId → seconds` — feeds the team
 * member list's stacked composition bar. One row per user, each
 * segment coloured by project.
 */
export function entriesByUserAndProject(
  entries: readonly TimeEntry[],
): Map<string, Map<string, number>> {
  const out = new Map<string, Map<string, number>>();
  for (const e of entries) {
    let inner = out.get(e.userId);
    if (!inner) {
      inner = new Map<string, number>();
      out.set(e.userId, inner);
    }
    inner.set(e.projectId, (inner.get(e.projectId) ?? 0) + (e.durationSeconds ?? 0));
  }
  return out;
}

// ─── Latest entry per user (for "Latest activity" column) ─────────

/**
 * Per-user latest `TimeEntry` by `startTime`. Used in the team
 * member list to surface "what were they working on last" and
 * "how long ago" — a strong compliance signal on its own even
 * before the stacked bar.
 */
export function latestEntryByUser(
  entries: readonly TimeEntry[],
): Map<string, TimeEntry> {
  const out = new Map<string, TimeEntry>();
  for (const e of entries) {
    const current = out.get(e.userId);
    if (!current || e.startTime.toMillis() > current.startTime.toMillis()) {
      out.set(e.userId, e);
    }
  }
  return out;
}

// ─── Top / ranked helpers for the KPI cards + ranked list ─────────

/**
 * Pick the `(key, value)` pair with the largest value from a
 * `Map<K, number>`. Ties break on insertion order (first wins) so
 * behaviour is deterministic when two projects tie.
 */
export function topByValue<K>(m: Map<K, number>): { key: K; value: number } | null {
  let best: { key: K; value: number } | null = null;
  for (const [key, value] of m.entries()) {
    if (best === null || value > best.value) {
      best = { key, value };
    }
  }
  return best;
}

export interface RankedRow<K> {
  key: K;
  value: number;
  /** Percentage of the map's total, 0 when the total is zero. */
  percent: number;
}

/**
 * Rank the map desc by value and compute each row's share of the
 * total as a percentage (0–100). Protects against divide-by-zero:
 * if `total === 0`, every row's `percent` is `0` rather than `NaN`.
 *
 * Used for the donut's legend + the ranked project list.
 */
export function rankedByValue<K>(m: Map<K, number>): RankedRow<K>[] {
  const total = [...m.values()].reduce((s, v) => s + v, 0);
  const rows: RankedRow<K>[] = [...m.entries()].map(([key, value]) => ({
    key,
    value,
    percent: total > 0 ? (value / total) * 100 : 0,
  }));
  rows.sort((a, b) => b.value - a.value);
  return rows;
}
